import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { UPDATE_DISCIPLINE } from '../../Reducers/Actions';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import FormInput from '../../SystemDesign/FormInput';
import ColorSelector from '../../SystemDesign/ColorSelector';
import IconSelector from '../../SystemDesign/IconSelector';
import { useUpdateDisciplineMutation } from '../../services/SkillsManagement';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import MuiDialog from '../../SystemDesign/MuiDialog';
import EditIcon from '@mui/icons-material/Edit';
import { parseIconData } from '../../Utils/iconMapping.js';
import { Shield, Wrench, Anchor, Zap, BookOpen } from "lucide-react";

const COLOR_OPTIONS = [
  { name: 'red', value: '#F44336' },
  { name: 'orange', value: '#FF9800' },
  { name: 'yellow', value: '#FFC107' },
  { name: 'green', value: '#4CAF50' },
  { name: 'blue', value: '#2196F3' },
  { name: 'purple', value: '#9C27B0' },
  { name: 'pink', value: '#E91E63' },
  { name: 'cyan', value: '#00BCD4' },
];

const ICON_OPTIONS = [
  { name: 'QHSE', icon: Shield, displayName: 'QHSE', lucideName: 'Shield' },
  { name: 'Drilling', icon: Wrench, displayName: 'Drilling', lucideName: 'Wrench' },
  { name: 'Marine', icon: Anchor, displayName: 'Marine', lucideName: 'Anchor' },
  { name: 'Electrical', icon: Zap, displayName: 'Electrical', lucideName: 'Zap' },
  { name: 'Mechanic', icon: Wrench, displayName: 'Mechanic', lucideName: 'Wrench' },
  { name: 'Core', icon: BookOpen, displayName: 'Core', lucideName: 'BookOpen' },
];

const UpdateDiscipline = ({ open, onClose, discipline, onSubmit, refetchSkills }) => {
  const dispatch = useDispatch();
  
  // Access the SnackBar context
  const { setOpenSnackBar, setInfoAlert } = useContext(SnackBarContext);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // State to track form validity and dirty state
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  
  // Create a ref to access Formik methods from outside the Formik component
  const formRef = useRef(null);
  
  // Get initial color from localStorage or default to blue
  /**
   * Retrieves the initial color for a discipline from localStorage.
   * If the discipline does not exist or an error occurs, it defaults to blue.
   * 
   * @returns {string} - The color value for the discipline.
   */
  const getInitialColor = () => {
    if (!discipline) return COLOR_OPTIONS[4].value; // Default blue
    
    try {
      const disciplineColors = JSON.parse(localStorage.getItem('disciplineColors') || '{}');
      return disciplineColors[discipline.discipline_id] || discipline.color || COLOR_OPTIONS[4].value;
    } catch (error) {
      console.error('Error reading color from localStorage:', error);
      return discipline.color || COLOR_OPTIONS[4].value;
    }
  };
  
  /**
   * Gets the initial icon selection for the form based on backend data
   * @returns {string} - The icon name to select
   */
  const getInitialIconSelection = () => {
    if (!discipline?.icon) return 'QHSE';
    
    try {
      const IconComponent = parseIconData(discipline.icon);
      if (IconComponent) {
        const matchedOption = ICON_OPTIONS.find(option => option.icon === IconComponent);
        if (matchedOption) return matchedOption.name;
      }
    } catch (error) {
      console.error('Error parsing discipline icon:', error);
    }
    
    return 'QHSE';
  };
  
  const [selectedColor, setSelectedColor] = useState(getInitialColor());
  const [selectedIcon, setSelectedIcon] = useState(getInitialIconSelection());
  const [formError, setFormError] = useState('');
  
  // RTK Query mutation hook
  const [updateDisciplineMutation, { isLoading }] = useUpdateDisciplineMutation();
  
  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required('Department name is required')
  });

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    if (!discipline) return;
    
    try {
      setFormError('');
      // Find the selected icon object
      const selectedIconObj = ICON_OPTIONS.find(option => option.name === selectedIcon);
      
      // Create the structured icon object similar to the backend format
      const iconData = {
        type: 'Lucide',
        name: selectedIconObj ? selectedIconObj.lucideName : 'Shield',
        props: {
          size: 24,
          strokeWidth: 2
        }
      };

      // Call the API to update the discipline with name and icon
      const result = await updateDisciplineMutation({
        disciplineId: discipline.discipline_id,
        body: {
          name: values.name.trim(),
          icon: JSON.stringify(iconData)
        }
      }).unwrap();
      
      // Update color information in localStorage since API doesn't support it
      try {
        // Get existing disciplines colors or initialize empty object
        const disciplineColors = JSON.parse(localStorage.getItem('disciplineColors') || '{}');
        
        // Update this discipline's color using discipline_id as key
        disciplineColors[discipline.discipline_id] = selectedColor;
        
        // Save back to localStorage
        localStorage.setItem('disciplineColors', JSON.stringify(disciplineColors));
      } catch (storageError) {
        console.error('Error updating color in localStorage:', storageError);
        // Non-critical error, we can continue
      }
      
      // Update Redux store with complete discipline data
      dispatch({ 
        type: UPDATE_DISCIPLINE, 
        payload: {
          ...result,
          discipline_id: discipline.discipline_id,
          color: selectedColor,
          name: values.name.trim(),
          icon: JSON.stringify(iconData)
        }
      });
    
    // Show success message
    setInfoAlert({
      severity: 'success',
      message: `Department "${values.name.trim()}" successfully updated`
    });
    setOpenSnackBar(true);
    
    // If the onSubmit prop exists, call it with the updated discipline, color and icon
    if (onSubmit) {
      // Include the color and icon in the result object for the parent component
      onSubmit({
        ...result,
        color: selectedColor,
        icon: JSON.stringify(iconData)
      });
    }
    
    // Close dialog
    onClose();
  } catch (error) {
    console.error('Failed to update discipline:', error);
    const errorMessage = error?.data?.message || 'Failed to update discipline';
    
    // Set form error
    setFormError(errorMessage);
    
    // Also show in snackbar for better visibility
    setInfoAlert({
      severity: 'error',
      message: errorMessage
    });
    setOpenSnackBar(true);
    
    setSubmitting(false);
  }
  };

  // Update color and icon when discipline changes
  useEffect(() => {
    setSelectedColor(getInitialColor());
    setSelectedIcon(getInitialIconSelection());
  }, [discipline]);
  
  // Reset form state when dialog closes
  useEffect(() => {
    if (!open) {
      setIsFormValid(false);
      setIsFormDirty(false);
    }
  }, [open]);
  
  // Function to trigger form submission via MuiDialog action button
  const submitForm = useCallback(() => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  }, []);
  
  // Don't render if no discipline is provided
  if (!discipline) return null;

  // Create dialog content for MuiDialog
  const dialogContent = () => (
    <Formik
      initialValues={{ name: discipline?.name || '' }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
      innerRef={formRef}
    >
      {(formikProps) => {
        // Update form state for dialog actions
        const { isValid, dirty } = formikProps;
        setTimeout(() => {
          setIsFormValid(isValid);
          setIsFormDirty(dirty);
        }, 0);
        
        return (
          <Form id="update-discipline-form">
            {formError && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {formError}
              </Typography>
            )}
            
            {/* Form fields */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" fontWeight="500" mb={1}>
                Department Name
              </Typography>
              
              <FormInput
                name="name"
                placeholder="Ex: QHSE, Drilling..."
                variant="outlined"
              />
            </Box>
            
            {/* Color selector */}
            <ColorSelector 
              colors={COLOR_OPTIONS}
              selectedColor={selectedColor}
              onColorChange={setSelectedColor}
            />
            
            {/* Icon selector */}
            <IconSelector 
              icons={ICON_OPTIONS}
              selectedIcon={selectedIcon}
              onIconChange={setSelectedIcon}
            />
          </Form>
        );
      }}
    </Formik>
  );

  return (
    <MuiDialog 
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      icon={<EditIcon />}
      title="Update Department"
      //subtitle="Edit discipline details"
      content={dialogContent}
      existAction={true}
      actionTitle="Update Department"
      actionFunction={submitForm}
      isLoading={isLoading}
      actionType="primary"
      formId="update-discipline-form"
      disableAction={!(isFormValid && isFormDirty)}
    />
  );
};

export default UpdateDiscipline;